import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../firebase/AuthContext';
import { getUserOrders } from '../../firebase/firestoreService';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const WelcomeSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
`;

const CardTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const CardContent = styled.div`
  color: #555;
`;

const StatsNumber = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: #4285f4;
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin: 2rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const Button = styled(Link)`
  display: inline-block;
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3367d6;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background-color: #f8f9fa;
  color: #333;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 1rem;
  border-top: 1px solid #eee;
  color: #555;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'background-color: #e6f4ea; color: #137333;';
      case 'processing':
        return 'background-color: #e8f0fe; color: #1a73e8;';
      case 'shipped':
        return 'background-color: #fef7e0; color: #b06000;';
      case 'pending':
      default:
        return 'background-color: #f1f3f4; color: #5f6368;';
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const EmptyStateText = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
`;

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchOrders = async () => {
      if (currentUser) {
        try {
          const { orders, error } = await getUserOrders(currentUser.uid);
          
          if (error) {
            console.error('Error fetching orders:', error);
            return;
          }
          
          setOrders(orders || []);
        } catch (error) {
          console.error('Error in order fetch:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchOrders();
  }, [currentUser]);
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  // Calculate total spent (only paid orders)
  const calculateTotalSpent = () => {
    if (!orders || orders.length === 0) return 0;
    
    return orders.reduce((total, order) => {
      // Only count orders that have been paid
      const isPaid = order.paymentStatus === 'paid' || 
                     order.status === 'completed' || 
                     order.status === 'shipped' ||
                     order.status === 'processing';
      
      if (!isPaid) return total;
      
      // Handle both old and new order structures
      if (order.orderDetails && order.orderDetails.total) {
        return total + order.orderDetails.total;
      } else if (order.total) {
        return total + order.total;
      }
      return total;
    }, 0);
  };

  // Calculate reward points (1 point per $10 spent on paid orders)
  const calculateRewardPoints = () => {
    const totalSpent = calculateTotalSpent();
    return Math.floor(totalSpent / 10);
  };

  // Get recent orders
  const getRecentOrders = () => {
    if (!orders || orders.length === 0) return [];
    return orders.slice(0, 5);
  };

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  return (
    <DashboardContainer>
      <WelcomeSection>
        <Title>Welcome, {currentUser.displayName || 'Collector'}!</Title>
        <Subtitle>Manage your collection and orders from your personal dashboard.</Subtitle>
        <Button to="/profile">View Profile</Button>
      </WelcomeSection>
      
      {/* Stats Cards */}
      <CardsContainer>
        <Card>
          <CardTitle>Total Orders</CardTitle>
          <CardContent>
            <StatsNumber>{orders.length}</StatsNumber>
            <div>All time</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardTitle>Total Spent</CardTitle>
          <CardContent>
            <StatsNumber>{formatCurrency(calculateTotalSpent())}</StatsNumber>
            <div>All time</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardTitle>🏆 Reward Points</CardTitle>
          <CardContent>
            <StatsNumber style={{ color: '#D4AF37' }}>{calculateRewardPoints()}</StatsNumber>
            <div>1 point per $10 spent</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardTitle>Wishlist Items</CardTitle>
          <CardContent>
            <StatsNumber>0</StatsNumber>
            <div>Items saved for later</div>
          </CardContent>
        </Card>
      </CardsContainer>
      
      {/* Leaderboard Link */}
      <Card style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <CardTitle>🏅 Rewards Leaderboard</CardTitle>
        <CardContent>
          <div style={{ marginBottom: '1rem' }}>See how you rank against other collectors!</div>
          <Button to="/leaderboard">View Leaderboard</Button>
        </CardContent>
      </Card>
      
      {/* Recent Orders Section */}
      <SectionTitle>Recent Orders</SectionTitle>
      {getRecentOrders().length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Order ID</Th>
              <Th>Date</Th>
              <Th>Total</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {getRecentOrders().map(order => (
              <tr key={order.id}>
                <Td>{order.id.substring(0, 8)}...</Td>
                <Td>{formatDate(order.createdAt)}</Td>
                <Td>
                  {formatCurrency(
                    order.orderDetails?.total || order.total || 0
                  )}
                </Td>
                <Td>
                  <StatusBadge status={order.status}>
                    {order.status || 'pending'}
                  </StatusBadge>
                </Td>
                <Td>
                  <Link to={`/orders/${order.id}`}>View Details</Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState>
          <EmptyStateText>You haven't placed any orders yet.</EmptyStateText>
          <Button to="/products">Start Shopping</Button>
        </EmptyState>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
